/**
 * Synced from convex/chain/workflowRun.ts (TEN-174: MCP separate build, cannot
 * import Convex code — the prior header cited convex/lib/workflowRun.ts, a path
 * that no longer exists; fixed in WP-480 S4, FEAT-1361).
 *
 * Drift guard: scripts/check-mcp-lib-drift.mjs compares every declaration shared
 * with the canonical file after normalizing the two legitimate per-surface seams
 * (mcpGatewayClientError ↔ workflowClientError; './' ↔ '../lib/' import paths).
 * Canonical-only declarations are limited to an explicit allowlist (the TEN-678
 * helper extractions — coerceWorkflowRunOutput / parseListOutput /
 * findClosestRoundId / levenshteinDistance); this surface deliberately keeps its
 * own interactive variants in tools/workflows.ts (different coercion semantics:
 * numbered-list parsing + whole-string fallback). A new canonical declaration
 * outside that allowlist fails the check until a human dispositions it.
 */

import {
	unknownWorkflowRoundMessage,
	workflowMustDefineStepMessage,
	workflowOutputFormatMismatchMessage,
	workflowRunNoActiveRoundMessage,
	workflowRunNotActiveMessage,
	workflowRunRoundMismatchMessage,
	workflowRunRoundNotActiveMessage,
} from './mcpGatewayClientMessages';
import { mcpGatewayClientError } from './errors';

/**
 * BET-85 Slice 2 — canonical workflow run domain model.
 *
 * Pure domain state for workflow execution. Persistence belongs in Convex
 * feature modules; MCP and UI consumers should adapt to this model rather than
 * inventing parallel run contracts.
 */

export type WorkflowRoundType =
  | "open"
  | "choice"
  | "synthesis"
  | "commit"
  | "close";

export type WorkflowOutputFormat =
  | "freetext"
  | "list"
  | "choice"
  | "structured";

type JsonPrimitive = string | number | boolean | null;
export type JsonValue =
  | JsonPrimitive
  | JsonValue[]
  | { [key: string]: JsonValue };

export type WorkflowRunOutput =
  | { format: "freetext"; value: string }
  | { format: "list"; value: string[] }
  | { format: "choice"; value: string | string[] }
  | { format: "structured"; value: JsonValue };

export type WorkflowRunStatus = "active" | "completed";
export type WorkflowRunStepStatus = "pending" | "active" | "completed";

export interface WorkflowRunStepDefinition {
  roundId: string;
  num: string;
  label: string;
  type: WorkflowRoundType;
  outputField: string;
  outputFormat: WorkflowOutputFormat;
}

export interface WorkflowRunCheckpoint {
  roundId: string;
  output: WorkflowRunOutput;
  recordedAt: string;
}

export interface WorkflowRunStepState extends WorkflowRunStepDefinition {
  status: WorkflowRunStepStatus;
  output?: WorkflowRunOutput;
  lastCheckpointAt?: string;
}

export interface WorkflowRunState {
  runSchemaVersion: 1;
  workflowId: string;
  workflowName: string;
  templateSlug: string;
  templateName: string;
  status: WorkflowRunStatus;
  startedAt: string;
  completedAt?: string;
  currentRoundId: string | null;
  steps: WorkflowRunStepState[];
  checkpoints: WorkflowRunCheckpoint[];
}

// BET-85 + STA-3: one shared mapping from persisted workflow run docs to the
// canonical run-state contract used by tools, queries, and future UI surfaces.
export interface PersistedWorkflowRunRecord {
  runSchemaVersion: number;
  workflowId: string;
  workflowName: string;
  templateSlug?: string;
  templateName?: string;
  status: WorkflowRunStatus;
  startedAt: string;
  completedAt?: string;
  currentRoundId?: string;
  steps: WorkflowRunState["steps"];
  checkpoints: WorkflowRunState["checkpoints"];
}

export interface WorkflowRunRenderProjection {
  workflowId: string;
  workflowName: string;
  templateSlug: string;
  templateName: string;
  status: WorkflowRunStatus;
  currentRoundId: string | null;
  completedCount: number;
  totalCount: number;
  steps: Array<{
    roundId: string;
    num: string;
    label: string;
    type: WorkflowRoundType;
    status: WorkflowRunStepStatus;
    outputField: string;
    outputFormat: WorkflowOutputFormat;
    output?: WorkflowRunOutput;
  }>;
  render: {
    title: string;
    subtitle: string;
    template: {
      slug: string;
      name: string;
      stratum: "system";
      stageMode: "fixed";
      stages: Array<{
        id: string;
        fieldKey: string;
        label: string;
        prompt: string;
        color?: string;
      }>;
      outputFormats: Array<{
        id: string;
        label: string;
      }>;
      audiencePresets: Array<{
        id: string;
        label: string;
      }>;
    };
    links: Record<string, string>;
    sections: Array<{
      key: string;
      fieldKey: string;
      label: string;
      color: string;
      content: string;
      status: WorkflowRunStepStatus;
      roundId: string;
      roundNum: string;
      roundType: WorkflowRoundType;
      outputFormat: WorkflowOutputFormat;
      output?: WorkflowRunOutput;
    }>;
  };
}

export function createWorkflowRunState(args: {
  workflowId: string;
  workflowName: string;
  templateSlug?: string;
  templateName?: string;
  steps: WorkflowRunStepDefinition[];
  startedAt?: string;
}): WorkflowRunState {
  if (args.steps.length === 0) {
    throw mcpGatewayClientError(workflowMustDefineStepMessage(args.workflowId));
  }

  return {
    runSchemaVersion: 1,
    workflowId: args.workflowId,
    workflowName: args.workflowName,
    templateSlug: args.templateSlug ?? args.workflowId,
    templateName: args.templateName ?? args.workflowName,
    status: "active",
    startedAt: args.startedAt ?? new Date().toISOString(),
    currentRoundId: args.steps[0]?.roundId ?? null,
    steps: args.steps.map((step, index) => ({
      ...step,
      status: index === 0 ? "active" : "pending",
    })),
    checkpoints: [],
  };
}

export function applyWorkflowCheckpoint(
  run: WorkflowRunState,
  checkpoint: WorkflowRunCheckpoint,
): WorkflowRunState {
  if (run.status !== "active") {
    throw mcpGatewayClientError(workflowRunNotActiveMessage(run.workflowId));
  }

  if (!run.currentRoundId) {
    throw mcpGatewayClientError(workflowRunNoActiveRoundMessage(run.workflowId));
  }

  if (checkpoint.roundId !== run.currentRoundId) {
    throw mcpGatewayClientError(
      workflowRunRoundMismatchMessage(
        run.workflowId,
        run.currentRoundId,
        checkpoint.roundId,
      ),
    );
  }

  const stepIndex = run.steps.findIndex((step) => step.roundId === checkpoint.roundId);
  if (stepIndex === -1) {
    throw mcpGatewayClientError(unknownWorkflowRoundMessage(checkpoint.roundId));
  }

  const step = run.steps[stepIndex];
  if (step.status !== "active") {
    throw mcpGatewayClientError(
      workflowRunRoundNotActiveMessage(run.workflowId, checkpoint.roundId),
    );
  }

  validateWorkflowRunOutput(step.outputFormat, checkpoint.output);

  const isLastStep = stepIndex === run.steps.length - 1;
  const willCompleteRun = isLastStep;

  const updatedSteps = run.steps.map((currentStep, index) => {
    if (index === stepIndex) {
      return {
        ...currentStep,
        status: "completed" as const,
        output: checkpoint.output,
        lastCheckpointAt: checkpoint.recordedAt,
      };
    }

    if (index === stepIndex + 1 && !willCompleteRun) {
      return {
        ...currentStep,
        status: "active" as const,
      };
    }

    return currentStep;
  });

  return {
    ...run,
    status: willCompleteRun ? "completed" : "active",
    completedAt: willCompleteRun ? checkpoint.recordedAt : run.completedAt,
    currentRoundId: willCompleteRun ? null : updatedSteps[stepIndex + 1]?.roundId ?? null,
    steps: updatedSteps,
    checkpoints: [...run.checkpoints, checkpoint],
  };
}

export function replayWorkflowRunState(args: {
  workflowId: string;
  workflowName: string;
  templateSlug?: string;
  templateName?: string;
  steps: WorkflowRunStepDefinition[];
  checkpoints: WorkflowRunCheckpoint[];
  startedAt?: string;
}): WorkflowRunState {
  let run = createWorkflowRunState({
    workflowId: args.workflowId,
    workflowName: args.workflowName,
    templateSlug: args.templateSlug,
    templateName: args.templateName,
    steps: args.steps,
    startedAt: args.startedAt,
  });

  for (const checkpoint of args.checkpoints) {
    run = applyWorkflowCheckpoint(run, checkpoint);
  }

  return run;
}

export function buildWorkflowRunRenderProjection(
  run: WorkflowRunState,
): WorkflowRunRenderProjection {
  const completedCount = run.steps.filter((step) => step.status === "completed").length;
  const renderSections = run.steps.map((step) => {
    const content = step.output ? workflowRunOutputToText(step.output) : "";

    return {
      key: step.roundId,
      fieldKey: step.outputField,
      label: step.label,
      color: getWorkflowRunStepColor(step.type),
      content,
      status: step.status,
      roundId: step.roundId,
      roundNum: step.num,
      roundType: step.type,
      outputFormat: step.outputFormat,
      output: step.output,
    };
  });
  const renderLinks = Object.fromEntries(
    renderSections
      .filter((section) => section.content.length > 0)
      .map((section) => [section.key, section.content]),
  );
  const subtitle =
    run.status === "completed"
      ? `${completedCount}/${run.steps.length} rounds completed`
      : `${completedCount}/${run.steps.length} rounds completed · current ${run.currentRoundId ?? "none"}`;

  return {
    workflowId: run.workflowId,
    workflowName: run.workflowName,
    templateSlug: run.templateSlug,
    templateName: run.templateName,
    status: run.status,
    currentRoundId: run.currentRoundId,
    completedCount,
    totalCount: run.steps.length,
    steps: run.steps.map((step) => ({
      roundId: step.roundId,
      num: step.num,
      label: step.label,
      type: step.type,
      status: step.status,
      outputField: step.outputField,
      outputFormat: step.outputFormat,
      output: step.output,
    })),
    render: {
      title: run.workflowName,
      subtitle,
      template: {
        slug: run.templateSlug,
        name: run.templateName,
        stratum: "system",
        stageMode: "fixed",
        stages: run.steps.map((step) => ({
          id: step.roundId,
          fieldKey: step.outputField,
          label: step.label,
          prompt: `Round ${step.num}: ${step.label}`,
          color: getWorkflowRunStepColor(step.type),
        })),
        outputFormats: [
          {
            id: "workflow-run",
            label: "Workflow Run",
          },
        ],
        audiencePresets: [],
      },
      links: renderLinks,
      sections: renderSections,
    },
  };
}

export function toWorkflowRunState(
  run: PersistedWorkflowRunRecord,
): WorkflowRunState {
  return {
    runSchemaVersion: run.runSchemaVersion as 1,
    workflowId: run.workflowId,
    workflowName: run.workflowName,
    templateSlug: run.templateSlug ?? run.workflowId,
    templateName: run.templateName ?? run.workflowName,
    status: run.status,
    startedAt: run.startedAt,
    completedAt: run.completedAt,
    currentRoundId: run.currentRoundId ?? null,
    steps: run.steps,
    checkpoints: run.checkpoints,
  };
}

export function workflowRunOutputToText(output: WorkflowRunOutput): string {
  switch (output.format) {
    case "freetext":
      return output.value;
    case "list":
      return output.value.join(", ");
    case "choice":
      return Array.isArray(output.value) ? output.value.join(", ") : output.value;
    case "structured":
      if (output.value && typeof output.value === "object" && "text" in output.value) {
        const textValue = (output.value as { text?: unknown }).text;
        if (typeof textValue === "string") {
          return textValue;
        }
      }
      return JSON.stringify(output.value);
  }
}

export function workflowRunOutputsEqual(
  left: WorkflowRunOutput,
  right: WorkflowRunOutput,
): boolean {
  if (left.format !== right.format) {
    return false;
  }

  return stableSerializeJsonValue(left.value) === stableSerializeJsonValue(right.value);
}

function validateWorkflowRunOutput(
  expectedFormat: WorkflowOutputFormat,
  output: WorkflowRunOutput,
): void {
  if (output.format !== expectedFormat) {
    throw mcpGatewayClientError(
      workflowOutputFormatMismatchMessage(expectedFormat, output.format),
    );
  }

  switch (output.format) {
    case "freetext":
      if (typeof output.value !== "string") {
        throw mcpGatewayClientError("Freetext workflow output must be a string.");
      }
      return;
    case "list":
      if (!Array.isArray(output.value) || output.value.some((item) => typeof item !== "string")) {
        throw mcpGatewayClientError("List workflow output must be a string array.");
      }
      return;
    case "choice":
      if (
        typeof output.value !== "string" &&
        (!Array.isArray(output.value) ||
          output.value.some((item) => typeof item !== "string"))
      ) {
        throw mcpGatewayClientError("Choice workflow output must be a string or string array.");
      }
      return;
    case "structured":
      if (!isJsonValue(output.value)) {
        throw mcpGatewayClientError("Structured workflow output must be JSON-serializable.");
      }
      return;
  }
}

function getWorkflowRunStepColor(type: WorkflowRoundType): string {
  switch (type) {
    case "open":
      return "var(--link-problem)";
    case "choice":
      return "var(--link-choice)";
    case "synthesis":
      return "var(--link-insight)";
    case "commit":
      return "var(--link-action)";
    case "close":
      return "var(--link-outcome)";
  }
}

function stableSerializeJsonValue(value: JsonValue): string {
  if (value === null || typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return JSON.stringify(value);
  }

  if (Array.isArray(value)) {
    return `[${value.map((item) => stableSerializeJsonValue(item)).join(",")}]`;
  }

  const entries = Object.entries(value).sort(([leftKey], [rightKey]) =>
    leftKey.localeCompare(rightKey),
  );

  return `{${entries
    .map(([key, entryValue]) =>
      `${JSON.stringify(key)}:${stableSerializeJsonValue(entryValue)}`,
    )
    .join(",")}}`;
}

function isJsonValue(value: unknown): value is JsonValue {
  if (
    value === null ||
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return true;
  }

  if (Array.isArray(value)) {
    return value.every((item) => isJsonValue(item));
  }

  if (typeof value === "object") {
    const prototype = Object.getPrototypeOf(value);
    if (prototype !== Object.prototype && prototype !== null) {
      return false;
    }

    return Object.values(value as Record<string, unknown>).every((item) =>
      isJsonValue(item),
    );
  }

  return false;
}
